import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import * as types from '@Constants/actionTypes';
import Notification from '@Common/Notification';
import DatePicker from '@Common/DatePicker/DatePicker';
import { actions as projectActions } from '@Actions/projectActions';
import { actions as projectLocationActions } from '@Actions/projectLocationActions';
import { ProjectLocation } from '@Types/ProjectLocation';

import './ChangeZeroDateDrawerModal.scss';

interface Props {
	projectLocationActions: typeof projectLocationActions;
	projectActions: typeof projectActions;
	selectedProjectLocations: ProjectLocation[];
	projectKey: string;
	onComplete: (skippedSomeLocations: boolean) => void;
	onCancel: () => void;
	onDataChange: (data: { selectedZeroDate: string; epicDates: { projectEpicLabelId: string; value: Date }[]; selectedRadioOption: string; isFormValid: boolean }) => void;
}

const ChangeZeroDateDrawerModal: React.FC<Props> = ({
	projectLocationActions,
	projectActions,
	selectedProjectLocations,
	projectKey,
	onComplete,
	onCancel,
	onDataChange,
}) => {
	const [selectedZeroDate, setSelectedZeroDate] = useState<string>('');
	const [isFormValid, setIsFormValid] = useState<boolean>(true);
	const [projectEpicLabels, setProjectEpicLabels] = useState<{ id: string; name: string; value?: string }[]>([]);
	const [epicDates, setEpicDates] = useState<{ projectEpicLabelId: string; value: Date }[]>([]);
	const [selectedRadioOption, setSelectedRadioOption] = useState<string>('1');

	useEffect(() => {
		const fetchProject = async () => {
			try {
				const result: any = await projectActions.getProject(projectKey);
				if (result.type === types.GET_PROJECT_SUCCESS) {
					setProjectEpicLabels(result.payload.projectEpicLabels);
				} else {
					Notification('error', 'An Error Occurred', result.payload.message);
				}
			} catch (error) {
				Notification('error', 'An Error Occurred', String(error));
			}
		};
		fetchProject();
	}, [projectActions, projectKey]);

	// Notify parent when data changes
	useEffect(() => {
		onDataChange({
			selectedZeroDate,
			epicDates,
			selectedRadioOption,
			isFormValid
		});
	}, [selectedZeroDate, epicDates, selectedRadioOption, isFormValid]);

	// Validate form whenever relevant fields change
	useEffect(() => {
		const isValid = selectedZeroDate.trim() !== '' && selectedRadioOption !== '';
		setIsFormValid(isValid);
	}, [selectedZeroDate, selectedRadioOption]);

	const onChangeZeroDate = (date: string) => {
		setSelectedZeroDate(date);
	};

	// Helper function to format date in the expected format (D MMM YYYY)
	const formatDate = (date: Date): string => {
		const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
						'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		
		const day = date.getDate();
		const month = months[date.getMonth()];
		const year = date.getFullYear();
		
		return `${day} ${month} ${year}`;
	};

	const onChangeEpicDate = (calendarDate: string, epicLabel: string) => {
		const date = new Date(calendarDate);
		setEpicDates((prevEpicDates) => {
			const labelIndex = prevEpicDates.findIndex((label) => label.projectEpicLabelId === epicLabel);
			if (labelIndex !== -1) {
				return prevEpicDates.map((label, index) => (index === labelIndex ? { ...label, value: date } : label));
			} else {
				return [...prevEpicDates, { projectEpicLabelId: epicLabel, value: date }];
			}
		});
	};

	const onChangeRadioOption = (value: string) => {
		setSelectedRadioOption(value);
	};

	return (
		<div className='task-details-content-zero-date-modal col-12' id='accordion'>
			<div className='content-title d-flex align-items-center justify-content-between'>
				Date Settings
			</div>
			<form noValidate className={isFormValid ? '' : 'was-validated'}>
				<div className='row me-3'>
					<div className='col-6'>
						<div className='date-picker-container'>
							<DatePicker
								value={selectedZeroDate}
								name='zeroDate'
								label='Zero Date'
								onChange={(date) => onChangeZeroDate(date)}
								isRequired={true}
								errorMessage='Zero Date Required'
							/>
						</div>
						{
							projectEpicLabels.map((epicLabel, index) => {
								const epicDateObj = epicDates.find(
									d => d.projectEpicLabelId === epicLabel.id
								);

								const value = epicDateObj?.value ? formatDate(epicDateObj.value) : '';

								return (
									<div key={index} className='date-picker-container'>
										<DatePicker
											value={value}
											name={`${epicLabel.id}`}
											label={epicLabel.name}
											onChange={(date) => onChangeEpicDate(date, epicLabel.id)}
										/>
									</div>
								);
							})
						}
					</div>
					<div className='col-6'>
						<div className='change-date-options'>
							<label className='title mb-2'>How should task dates be updated?</label>
							<div className='form-check'>
								<input 
									className='form-check-input' 
									type='radio' 
									name='radioOptions' 
									id='radio1' 
									value='1'
									checked={selectedRadioOption === '1'}
									onChange={(e) => onChangeRadioOption(e.target.value)}
								/>
								<label className='form-check-label' htmlFor='radio1'>
									<b>Simple Shift.</b> Shift all location tasks by the same number of days; the difference between the old and new Zero Date.
								</label>
							</div>
							<div className='form-check'>
								<input 
									className='form-check-input' 
									type='radio' 
									name='radioOptions' 
									id='radio2' 
									value='2'
									checked={selectedRadioOption === '2'}
									onChange={(e) => onChangeRadioOption(e.target.value)}
								/>
								<label className='form-check-label' htmlFor='radio2'>
									<b>Keep Custom Dates.</b> Shift all location tasks by the same number of days, except for tasks that have been customized.
								</label>
							</div>
							<div className='form-check'>
								<input 
									className='form-check-input' 
									type='radio' 
									name='radioOptions' 
									id='radio3' 
									value='3'
									checked={selectedRadioOption === '3'}
									onChange={(e) => onChangeRadioOption(e.target.value)}
								/>
								<label className='form-check-label' htmlFor='radio3'>
									<b>Full Reset.</b> Reset all location tasks dates to match the baseline.
								</label>
							</div>
							<p><a href='https://ehsolution.zendesk.com/hc/en-us/articles/28609102700699-How-to-Update-a-Location-s-Zero-Date' target='_blank'>How does this work?</a></p>
						</div>
					</div>
				</div>
			</form>
		</div>
	);
};

const mapDispatchToProps = (dispatch: Dispatch) => {
	return {
		projectActions: bindActionCreators(projectActions, dispatch),
		projectLocationActions: bindActionCreators(projectLocationActions, dispatch),
	};
};

export default connect(null, mapDispatchToProps)(ChangeZeroDateDrawerModal);
